import { TaskEither } from "fp-ts/TaskEither";
import { IO } from "fp-ts/IO";
import { Rework } from "../StandardLoanState";
import { useCommand, useQuery } from "../../useAPI";
import { ExternalLoansList, getRepaidLoans, submitRepaidLoans } from "../api";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { constant, constNull, pipe } from "fp-ts/function";
import { option, taskEither } from "fp-ts";
import * as remoteData from "../../RemoteData";
import { MainContent } from "../../Common/MainContent";
import {
  Body,
  Box,
  Card,
  ContentRow,
  Divider,
  ErrorBanner,
  Heading,
  Loader,
  Space,
  Stack,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { PageHeading } from "../../Common/PageHeading/PageHeading";
import { BackButton } from "../../Common/BackButton/BackButton";
import { Option } from "fp-ts/Option";
import { StepCommentsOutput } from "../Rework/api";
import { ReworkBanner } from "../Rework/ReworkBanner";
import { useRemoteData } from "../../useRemoteData";
import { useMemo } from "react";
import { useRepaidLoansStepChanges } from "../Rework/utils";
import * as reworkApi from "../Rework/api";
import { RepaidLiabilitiesEditForm } from "./RepaidLiabilitiesEditForm";

type Props = {
  onConfirm: TaskEither<unknown, unknown>;
  onBack: IO<unknown>;
  canGoBack: boolean;
  reworkData: Rework;
};

export const RepaidLoans = (props: Props) => {
  const isMobileLayout = useIsMobileLayout();
  const formatMessage = useFormatMessage();
  const stepComments = useCommand(reworkApi.stepComments);
  const [repaidLoans] = useQuery(getRepaidLoans);
  const submitLoans = useCommand(submitRepaidLoans);

  const reworkedAccounts = pipe(
    props.reworkData.oldValues,
    option.chain(oldValues => oldValues.repaidLoans),
    option.chain(repaidLoans => repaidLoans.loans)
  );

  const getAccountsForRefinancingStepChanges = useRepaidLoansStepChanges(
    pipe(
      repaidLoans,
      remoteData.fold(
        constant([]),
        constant([]),
        response => response.liabilitiesList
      )
    ),
    pipe(
      reworkedAccounts,
      option.map(v => v.liabilitiesList)
    )
  );

  const comments = useRemoteData(
    useMemo(() => stepComments({ reworkCategories: ["CONTRACT"] }), [])
  );

  const reworkComments = pipe(
    comments,
    remoteData.fold(
      () => option.none,
      () => option.none,
      comments => option.some(comments)
    )
  );

  const reworkBanner = (comments: Option<StepCommentsOutput>) => {
    return pipe(
      props.reworkData.oldValues,
      option.fold(constNull, () => (
        <ContentRow type="lateral-margins">
          <ReworkBanner
            changes={getAccountsForRefinancingStepChanges}
            stepComments={comments}
          />
        </ContentRow>
      ))
    );
  };

  const areReworkChanges =
    option.isSome(getAccountsForRefinancingStepChanges) &&
    getAccountsForRefinancingStepChanges.value.length > 0;

  const areReworkComments =
    option.isSome(reworkComments) &&
    option.isSome(reworkComments.value.stepComments) &&
    reworkComments.value.stepComments.value.length > 0;

  const externalLiabilities = (accounts: ExternalLoansList) => (
    <Stack column units={4}>
      <Space units={isMobileLayout ? 2 : 10} />
      <Body size="small" weight="medium">
        {formatMessage("StandardLoan.RepaidLoans.RepaidLiabilities.title")}
      </Body>
      <Body size="x-small" weight="regular">
        {formatMessage("StandardLoan.RepaidLoans.RepaidLiabilities.subTitle")}
      </Body>
      <RepaidLiabilitiesEditForm
        liabilities={accounts.liabilitiesList}
        onSubmit={externalLoansList =>
          pipe(
            submitLoans(externalLoansList),
            taskEither.chain(() => props.onConfirm)
          )
        }
        oldValues={reworkedAccounts}
      />
    </Stack>
  );

  const CardWrapper = (props: { children: React.ReactElement<unknown> }) => {
    return isMobileLayout ? (
      <>{props.children}</>
    ) : (
      <Card grow>{props.children}</Card>
    );
  };

  const ContentWrapper = (props: { children: React.ReactElement<unknown> }) => {
    if (isMobileLayout) {
      return <ContentRow type="full">{props.children}</ContentRow>;
    } else {
      return (
        <ContentRow type="lateral-margins">
          <Box column>
            <PageHeading
              title={formatMessage("StandardLoan.RepaidLoans.title")}
              hideOnMobile
              contentRowType={"full"}
            />
            {(areReworkChanges || areReworkComments) &&
              reworkBanner(reworkComments)}
            <Card>{props.children}</Card>
          </Box>
        </ContentRow>
      );
    }
  };

  return (
    <MainContent>
      <Box grow shrink column>
        {isMobileLayout && <Space units={6} />}

        {(areReworkChanges || areReworkComments) &&
          isMobileLayout &&
          reworkBanner(reworkComments)}

        <ContentWrapper>
          <CardWrapper>
            <Stack column units={4} grow shrink>
              <Heading size="small" weight="medium">
                {formatMessage("StandardLoan.RepaidLoans.subTitle")}
              </Heading>
              <Divider />
              {pipe(
                repaidLoans,
                remoteData.fold(
                  () => (
                    <Loader
                      ariaLabelMessage={formatMessage(
                        "generic.loading.alt" as RuntimeLocaleKey
                      )}
                    />
                  ),
                  () => (
                    <ErrorBanner>{formatMessage("GenericError")}</ErrorBanner>
                  ),
                  resposne => <>{externalLiabilities(resposne)}</>
                )
              )}
            </Stack>
          </CardWrapper>
        </ContentWrapper>
        {props.canGoBack && (
          <>
            <Space units={10} />
            <ContentRow type={"lateral-margins"}>
              <Box grow column={isMobileLayout}>
                <BackButton action={props.onBack} />
              </Box>
            </ContentRow>
          </>
        )}
      </Box>
    </MainContent>
  );
};
